import { Head, useForm } from "@inertiajs/react"
import { Camera } from "lucide-react"
import { useState } from "react"

import FileUpload from "@/components/file-upload"
import LocationSelect from "@/components/location-select"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import AppLayout from "@/layouts/app-layout"
import type ClubProfile from "@/types/serializers/ClubProfile"
import type RequestsUpdateClubProfile from "@/types/serializers/RequestsUpdateClubProfile"

export default function ClubEdit({ club }: { club: ClubProfile }) {
  const [avatarUploaded, setAvatarUploaded] = useState(false)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(
    club.avatar_url as string | null,
  )

  const { data, setData, patch, processing, errors } =
    useForm<RequestsUpdateClubProfile>({
      club_profile: {
        name: club.name,
        description: club.description ?? "",
        division: club.division ?? "",
        country: club.country ?? "",
        region: club.region ?? "",
        city: club.city ?? "",
        founded_year: club.founded_year ?? null,
        website_url: club.website_url ?? "",
        avatar: null,
      },
    })

  const setField = (
    field: keyof typeof data.club_profile,
    value: string | number | null,
  ) =>
    setData("club_profile", {
      ...data.club_profile,
      [field]: value,
    } as typeof data.club_profile)

  function handleCountryChange(newCountry: string) {
    setData("club_profile", {
      ...data.club_profile,
      country: newCountry,
      region: "",
      city: "",
    })
  }

  function handleRegionChange(newRegion: string) {
    setData("club_profile", {
      ...data.club_profile,
      region: newRegion,
      city: "",
    })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    patch("/perfil")
  }

  return (
    <AppLayout>
      <Head title="Editar perfil del Club | KrownFT" />

      <div className="mx-auto max-w-2xl px-6 py-8 pb-24">
        <h1 className="mb-6 text-2xl font-bold">Editar perfil del Club</h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          <section>
            <h2 className="mb-4 text-lg font-semibold">Logo del Club</h2>
            <div className="flex items-center gap-6">
              <div className="group relative">
                <Avatar className="h-24 w-24">
                  {avatarPreview ? (
                    <AvatarImage
                      src={avatarPreview}
                      alt="Logo actual"
                      className="object-cover"
                    />
                  ) : null}
                  <AvatarFallback className="bg-krown-600 text-2xl font-bold text-white">
                    {club.name[0]}
                  </AvatarFallback>
                </Avatar>
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                  <Camera className="h-6 w-6 text-white" />
                </div>
              </div>
              <div className="flex-1">
                <FileUpload
                  accept="image"
                  onUploadComplete={(signedId, file) => {
                    setField("avatar", signedId)
                    setAvatarUploaded(true)
                    const reader = new FileReader()
                    reader.onload = (e) =>
                      setAvatarPreview(e.target?.result as string)
                    reader.readAsDataURL(file)
                  }}
                />
                {avatarUploaded && (
                  <p className="mt-2 text-xs text-green-400">
                    Logo subido correctamente
                  </p>
                )}
                {errors["club_profile.avatar"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["club_profile.avatar"]}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* Información del Club */}
          <section>
            <h2 className="mb-4 text-lg font-semibold">Información del Club</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <Label className="mb-1.5 block">Nombre del Club</Label>
                <Input
                  value={data.club_profile.name}
                  onChange={(e) => setField("name", e.target.value)}
                />
              </div>
              <div>
                <Label className="mb-1.5 block">Año de Fundación</Label>
                <Input
                  type="number"
                  value={String(data.club_profile.founded_year ?? "")}
                  onChange={(e) =>
                    setField(
                      "founded_year",
                      parseInt(e.target.value, 10) || null,
                    )
                  }
                />
              </div>
              <div>
                <Label className="mb-1.5 block">División</Label>
                <Input
                  value={String(data.club_profile.division ?? "")}
                  onChange={(e) => setField("division", e.target.value)}
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="mb-1.5 block">Sitio Web</Label>
                <Input
                  type="url"
                  value={String(data.club_profile.website_url ?? "")}
                  onChange={(e) => setField("website_url", e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* Ubicación */}
          <section>
            <h2 className="mb-4 text-lg font-semibold">Ubicación</h2>
            <LocationSelect
              country={String(data.club_profile.country)}
              region={String(data.club_profile.region)}
              city={String(data.club_profile.city)}
              onCountryChange={handleCountryChange}
              onRegionChange={handleRegionChange}
              onCityChange={(v) => setField("city", v)}
              errors={{}}
            />
          </section>

          {/* Descripción */}
          <section>
            <h2 className="mb-4 text-lg font-semibold">Descripción</h2>
            <Textarea
              value={String(data.club_profile.description ?? "")}
              onChange={(e) => setField("description", e.target.value)}
              rows={5}
            />
          </section>

          <Button type="submit" disabled={processing} className="w-full">
            Guardar cambios
          </Button>
        </form>
      </div>
    </AppLayout>
  )
}
